"use client";

import { useId, useState } from "react";
import { isSatNumericResponse, normalizeNumericMinus, numericDraftError, numericResponseError, numericResponseLimit } from "../question-presentation/numeric-response";

export function SatNumericDirections() {
  return <div className="sat-numeric-directions">
    <p>Enter one number using digits, a fraction or a decimal. Negative answers are allowed.</p>
    <ul>
      <li>A positive answer has room for 5 characters. A negative answer has room for 6, including its leading minus sign. The decimal point and fraction bar each use one character.</li>
      <li>Use an improper fraction or decimal for a mixed number: enter <strong>7/2</strong> or <strong>3.5</strong> for three and one half.</li>
      <li>If a fraction is too long, use a decimal. If a decimal is too long, fill the available space by rounding or truncating it.</li>
      <li>Leave out spaces, commas, percent signs, currency symbols and units. Enter a numeric value rather than a calculation or expression.</li>
    </ul>
    <p>For example, 2/3 can be entered as <strong>2/3, .6666, .6667, 0.666 or 0.667</strong>. The shorter approximations .66 and .67 do not receive credit.</p>
  </div>;
}

export function SatNumericInput({ value, onChange, disabled = false, className = "spr-field" }: {
  value: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const [blockedEntry, setBlockedEntry] = useState("");
  const current = value ?? "";
  const error = blockedEntry || numericResponseError(current);
  const complete = isSatNumericResponse(current);
  const parts = normalizeNumericMinus(current).split("/");
  const readOnly = disabled || !onChange;
  return <div className="sat-numeric-input">
    {!readOnly && <details className="sat-entry-directions" open><summary>Answer-entry directions</summary><SatNumericDirections /></details>}
    <label className={className} htmlFor={id}>
      <span>Your answer</span>
      <input id={id} type="text" inputMode="text" autoComplete="off" autoCorrect="off" spellCheck={false}
        disabled={readOnly} maxLength={6} value={current} placeholder="Enter a number or fraction"
        aria-invalid={Boolean(error)} aria-describedby={`${id}-hint${error ? ` ${id}-error` : ""}`}
        onChange={event => {
          const next = normalizeNumericMinus(event.target.value);
          const message = numericDraftError(next);
          setBlockedEntry(message ?? "");
          if (!message) onChange?.(next);
        }}
        onPaste={event => {
          const input = event.currentTarget;
          const pasted = normalizeNumericMinus(event.clipboardData.getData("text"));
          const next = current.slice(0, input.selectionStart ?? 0) + pasted + current.slice(input.selectionEnd ?? current.length);
          const message = numericDraftError(next);
          // Reject the complete paste rather than silently clipping or changing its value.
          event.preventDefault();
          setBlockedEntry(message ?? "");
          if (!message) onChange?.(next);
        }} />
    </label>
    <small id={`${id}-hint`} className="sat-entry-hint">{current.length}/{numericResponseLimit(current)} characters · digits, decimal point, fraction bar, leading minus</small>
    {error && <p id={`${id}-error`} className="sat-entry-error" role="status">{error}</p>}
    {complete && <div className="sat-answer-preview"><span>Answer preview:</span>{parts.length === 2
      ? <span role="math" aria-label={current.replace("/", " divided by ")} className="sat-fraction"><span aria-hidden="true">{parts[0]}</span><span aria-hidden="true">{parts[1]}</span></span>
      : <strong>{current}</strong>}</div>}
  </div>;
}
